import { useState } from 'react';
import { Modal, Tabs } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay } from '@fortawesome/pro-light-svg-icons';
import { DEFAULT_BOX_SETTINGS, enabledMechanisms, primaryMechanism } from '@salaries/shared';
import { useAuth } from './auth/AuthContext';
import { Loader } from './components/Loader';
import { GearIcon } from './components/icons';
import { BoxSettingsDrawer } from './features/settings/BoxSettingsDrawer';
import { StaffSalarySettingsPage } from './features/staff-settings/StaffSalarySettingsPage';
import { PayrollReportTab, type ClosedRunRef } from './features/payroll/PayrollReportTab';
import { ClosedRunsTab } from './features/payroll/ClosedRunsTab';
import { OnboardingWizard } from './features/onboarding/OnboardingWizard';

type Tab = 'report' | 'closed-runs' | 'staff-settings';

export default function App() {
  const auth = useAuth();
  const [tab, setTab] = useState<Tab>('report');
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Explainer-video lightbox (embedded YouTube player) next to the page title.
  const [videoOpen, setVideoOpen] = useState(false);
  // After closing a period, deep-link into its frozen report (closed-runs tab).
  const [closedRun, setClosedRun] = useState<ClosedRunRef | null>(null);

  if (auth.status === 'loading') return <Loader fullScreen />;

  if (auth.status === 'unauthenticated' || auth.status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-grey-bg p-8">
        <div className="text-center">
          <div className="text-main-text mb-2 font-semibold" style={{ fontSize: '1.625rem' }}>
            לא מחובר
          </div>
          <div className="text-small text-secondary-text">{auth.error}</div>
          {/* DEV ONLY — stripped from production builds (import.meta.env.DEV). */}
          {import.meta.env.DEV && (
            <button
              type="button"
              onClick={() => void auth.devLogin()}
              className="mt-5 px-4 py-2 rounded bg-primary text-white text-small font-medium hover:bg-primary-hover"
            >
              כניסת פיתוח (נתוני דמו)
            </button>
          )}
        </div>
      </div>
    );
  }

  // Deactivated plan: block the whole add-on (the server 403s every functional
  // route anyway — this is the friendly screen).
  if (auth.plan === 'deactivated') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-grey-bg p-8">
        <div className="text-center max-w-sm">
          <div className="text-main-text mb-2 font-semibold" style={{ fontSize: '1.625rem' }}>
            מודול השכר אינו פעיל
          </div>
          <div className="text-small text-secondary-text">
            הגישה למודול השכר אינה פעילה עבור העסק. לפרטים והפעלה יש לפנות לצוות Arbox.
          </div>
        </div>
      </div>
    );
  }

  // First-time user: gate the app behind the onboarding wizard until completed.
  if (!auth.onboardingCompleted) return <OnboardingWizard />;

  const mechanism = auth.settings ? primaryMechanism(auth.settings) : 'group_classes';
  // A box with 2+ mechanisms gets one consolidated report across all of them,
  // so naming it after the primary one ("לפי חוגים") misdescribes it.
  const multiMechanism = auth.settings ? enabledMechanisms(auth.settings).length > 1 : false;
  const reportLabel = multiMechanism
    ? 'דוח שכר'
    : mechanism === 'hugim'
      ? 'דוח שכר לפי חוגים'
      : mechanism === 'time_clock'
        ? 'דוח שכר לפי נוכחות'
        : 'דוח שכר לפי שיעורים';

  return (
    <div className="min-h-screen flex flex-col bg-grey-bg">
      <main className="flex-1 min-h-0 flex flex-col px-4 lg:px-8 pt-3 lg:pt-5 pb-6">
        {/* Page header — title on the RTL start (right), action cluster on the
            end (left). Gear opens global box-level settings (added next step). */}
        <div className="flex items-center justify-between gap-2 sticky top-0 z-20 -mx-4 px-4 py-2 bg-grey-bg sm:static sm:mx-0 sm:px-0 sm:py-0 sm:bg-transparent">
          <div className="flex items-center gap-3">
            <h1 className="text-xl sm:text-[1.625rem] font-semibold text-main-text" dir="auto">
              ניהול שכר מתקדם
            </h1>
            {/* Play icon → explainer video (opens in an in-page lightbox). */}
            <button
              type="button"
              onClick={() => setVideoOpen(true)}
              aria-label="סרטון הסבר על ניהול שכר מתקדם"
              title="צפו בהסבר על הפיצ׳ר"
              className="inline-flex items-center justify-center shrink-0 text-secondary-text hover:text-main-text transition-colors cursor-pointer"
            >
              <FontAwesomeIcon icon={faPlay} flip="horizontal" style={{ fontSize: 18 }} />
            </button>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              aria-label="הגדרות"
              title="הגדרות"
              className="flex items-center justify-center p-2 rounded hover:bg-grey-bg transition-colors"
              onClick={() => setSettingsOpen(true)}
            >
              <GearIcon />
            </button>
          </div>
        </div>

        <Tabs
          activeKey={tab}
          onChange={(k) => setTab(k as Tab)}
          destroyInactiveTabPane
          className="salaries-tabs mt-3 sm:mt-4 flex-1 min-h-0"
          items={[
            {
              key: 'report',
              label: reportLabel,
              children: (
                <PayrollReportTab
                  onPeriodClosed={(run) => {
                    setClosedRun(run);
                    setTab('closed-runs');
                  }}
                />
              ),
            },
            {
              key: 'closed-runs',
              label: 'דוחות סגורים',
              children: (
                <ClosedRunsTab
                  initialOpen={closedRun}
                  onConsumed={() => setClosedRun(null)}
                />
              ),
            },
            {
              key: 'staff-settings',
              label: 'הגדרות שכר פרטניות',
              children: <StaffSalarySettingsPage />,
            },
          ]}
        />
      </main>

      <BoxSettingsDrawer
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        initial={auth.settings ?? DEFAULT_BOX_SETTINGS}
        onSaved={() => {
          void auth.refetchMe();
        }}
      />

      {/* In-page explainer-video lightbox. destroyOnClose stops playback on close. */}
      <Modal
        open={videoOpen}
        onCancel={() => setVideoOpen(false)}
        footer={null}
        centered
        destroyOnClose
        width={880}
        title="ניהול שכר מתקדם — סרטון הסבר"
        styles={{ body: { padding: 0 } }}
      >
        <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
          <iframe
            src="https://www.youtube-nocookie.com/embed/JUz574MLTBY?rel=0&autoplay=1"
            title="ניהול שכר מתקדם — סרטון הסבר"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            // The app sends Referrer-Policy: no-referrer; YouTube needs the origin
            // to validate the embed (else "Error 153"). Override per-iframe so it
            // gets the origin without relaxing the app-wide policy.
            referrerPolicy="strict-origin-when-cross-origin"
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
          />
        </div>
      </Modal>
    </div>
  );
}
